import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import messageService from '../../utils/message-service'
import { getWindowContext } from '../../utils/popup-utils'

/**
 * Screen component that displays when session has timed out
 * @param {Object} props - Component props
 * @param {boolean} props.isVisible - Whether the screen is visible
 * @param {Object} props.config - Configuration object for styling and text
 * @param {Function} props.onClose - Callback when screen should close
 * @param {string} props.scenario - Scenario type: 'sessionExpired' or 'paymentLinkExpired'
 */
const SessionTimeoutScreen = ({ isVisible, config = {}, onClose, scenario = 'sessionExpired' }) => {
    const [countdown, setCountdown] = useState(5)

    const {
        title = 'Session Expired',
        message = 'Your session has expired due to inactivity. This window will close automatically.',
        countdownMessage = 'Closing in {countdown} seconds...',
        primaryColor = '#007bff',
        backgroundColor = '#ffffff',
        textColor = '#333333',
        overlayColor = 'rgba(0, 0, 0, 0.8)',
        autoCloseDelay = 5000, // 5 seconds
    } = config

    useEffect(() => {
        if (!isVisible) return

        // Send appropriate message based on scenario
        const messageText =
            scenario === 'paymentLinkExpired' ? 'Your payment link has expired.' : 'Your session has expired due to inactivity.'

        messageService.sendSessionExpired(messageText, {
            reason: scenario === 'paymentLinkExpired' ? 'Payment link expired' : 'Session expired due to inactivity',
            autoClosing: true,
            scenario,
        })

        // Get window context to determine behavior
        const windowContext = getWindowContext()
        const isPopup = windowContext === 'popup'

        // Start countdown
        const countdownInterval = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(countdownInterval)
                    if (onClose) {
                        onClose()
                    }
                    return 0
                }
                return prev - 1
            })
        }, 1000)

        // Auto-close after delay
        const autoCloseTimeout = setTimeout(() => {
            // Send final window closing message
            messageService.sendWindowClosing('Your payment link has expired - auto closing', {
                reason: 'Auto-close after session timeout',
                windowContext,
                isPopup,
            })

            if (onClose) {
                onClose()
            }
        }, autoCloseDelay)

        return () => {
            clearInterval(countdownInterval)
            clearTimeout(autoCloseTimeout)
        }
    }, [isVisible, autoCloseDelay, onClose, scenario])

    if (!isVisible) {
        return null
    }

    const screenStyles = {
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: overlayColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000, // Higher than warning modal
        },
        container: {
            backgroundColor: backgroundColor,
            borderRadius: '8px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
            color: textColor,
        },
        title: {
            fontSize: '24px',
            fontWeight: 'bold',
            margin: '0 0 16px 0',
            color: textColor,
        },
        message: {
            fontSize: '16px',
            lineHeight: '1.5',
            margin: '0 0 24px 0',
            color: textColor,
        },
        countdown: {
            fontSize: '18px',
            fontWeight: '500',
            color: primaryColor,
            margin: '16px 0',
        },
        icon: {
            fontSize: '48px',
            color: '#ffc107',
            marginBottom: '16px',
        },
    }

    const formatCountdownMessage = () => {
        return countdownMessage.replace('{countdown}', countdown)
    }

    // Get window context for conditional rendering
    const windowContext = getWindowContext()
    const isPopup = windowContext === 'popup'

    // Show different messages based on window context and scenario
    const getDisplayMessage = () => {
        if (message) {
            return message // Use provided config message
        }

        // Fallback messages based on scenario and window context
        if (scenario === 'paymentLinkExpired') {
            if (isPopup) {
                return 'Your payment link has expired. This window will close automatically.'
            }
            return 'Your payment link has expired, please go back to your merchant and regenerate a payment link.'
        }

        // Default session expired messages
        if (isPopup) {
            return 'Your session has expired due to inactivity. This window will close automatically.'
        }
        return 'Your session has expired due to inactivity.'
    }

    const getDisplayTitle = () => {
        if (title) {
            return title // Use provided config title
        }

        // Fallback titles based on scenario
        return scenario === 'paymentLinkExpired' ? 'Payment Link Expired' : 'Session Expired'
    }

    const getDisplayIcon = () => {
        return scenario === 'paymentLinkExpired' ? '🔗' : '⏰'
    }

    return (
        <div style={screenStyles.overlay} data-testid="session-timeout-screen">
            <div style={screenStyles.container} role="dialog" aria-modal="true" aria-labelledby="timeout-screen-title">
                <div style={screenStyles.icon}>{getDisplayIcon()}</div>
                <h2 id="timeout-screen-title" style={screenStyles.title}>
                    {getDisplayTitle()}
                </h2>
                <p style={screenStyles.message}>{getDisplayMessage()}</p>
                {isPopup && (
                    <div style={screenStyles.countdown} data-testid="countdown-display">
                        {formatCountdownMessage()}
                    </div>
                )}
            </div>
        </div>
    )
}

SessionTimeoutScreen.propTypes = {
    isVisible: PropTypes.bool.isRequired,
    onClose: PropTypes.func,
    scenario: PropTypes.oneOf(['sessionExpired', 'paymentLinkExpired']),
    config: PropTypes.shape({
        title: PropTypes.string,
        message: PropTypes.string,
        countdownMessage: PropTypes.string,
        primaryColor: PropTypes.string,
        backgroundColor: PropTypes.string,
        textColor: PropTypes.string,
        overlayColor: PropTypes.string,
        autoCloseDelay: PropTypes.number,
    }),
}

export default SessionTimeoutScreen
